import { get, set, del } from 'idb-keyval';
import { queryClient, serializeQueryCache, restoreQueryCache } from './queryClient';
import { networkLogger } from './logger';

const IDB_CACHE_KEY = 'agxl-react-query-cache';
const PERSIST_DEBOUNCE_MS = 2000;

let persistTimeout: NodeJS.Timeout | null = null;

/**
 * Persist query cache to IndexedDB
 */
export const persistQueryCache = async (): Promise<void> => {
  try {
    const serialized = serializeQueryCache();
    await set(IDB_CACHE_KEY, {
      timestamp: Date.now(),
      data: serialized,
    });
    networkLogger.debug(`Persisted ${serialized.length} queries to IndexedDB`);
  } catch (error) {
    networkLogger.warn('Failed to persist query cache:', error);
  }
};

/**
 * Debounced persist to avoid excessive writes
 */
export const debouncedPersist = (): void => {
  if (persistTimeout) {
    clearTimeout(persistTimeout);
  }
  persistTimeout = setTimeout(() => {
    persistQueryCache();
  }, PERSIST_DEBOUNCE_MS);
};

/**
 * Restore query cache from IndexedDB
 */
export const restorePersistedCache = async (): Promise<boolean> => {
  try {
    const cached = await get(IDB_CACHE_KEY);
    
    if (!cached || !cached.data) {
      networkLogger.debug('No persisted cache found');
      return false;
    }
    
    // Check if cache is older than 7 days
    const maxAge = 1000 * 60 * 60 * 24 * 7;
    if (Date.now() - cached.timestamp > maxAge) {
      networkLogger.debug('Persisted cache expired, clearing');
      await clearPersistedCache();
      return false;
    }
    
    restoreQueryCache(cached.data);
    networkLogger.info(`Restored ${cached.data.length} queries from IndexedDB`);
    return true;
  } catch (error) {
    networkLogger.warn('Failed to restore query cache:', error);
    return false;
  }
};

/**
 * Clear persisted cache
 */
export const clearPersistedCache = async (): Promise<void> => {
  try {
    await del(IDB_CACHE_KEY);
    networkLogger.debug('Cleared persisted query cache');
  } catch (error) {
    networkLogger.warn('Failed to clear persisted cache:', error);
  }
};

/**
 * Setup automatic persistence on cache changes
 */
export const setupCachePersistence = (): (() => void) => {
  const cache = queryClient.getQueryCache();
  
  // Subscribe to cache changes
  const unsubscribe = cache.subscribe((event) => {
    // Only persist on successful data updates
    if (event.type === 'updated' && event.query.state.status === 'success') {
      debouncedPersist();
    }
  });
  
  // Persist on page unload
  const handleBeforeUnload = () => {
    persistQueryCache();
  };
  
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // Return cleanup function
  return () => {
    unsubscribe();
    window.removeEventListener('beforeunload', handleBeforeUnload);
    if (persistTimeout) {
      clearTimeout(persistTimeout);
    }
  };
};
